<template>
    <!-- UXB-5: in edit mode the authoring chrome (drag grip + kebab) is
         hover/focus-revealed; view mode keeps it always-visible. -->
    <div style="width:100%;height:100%" :class="{ 'cdb-hover-chrome': editMode, 'cdb-view-hover-chrome': !editMode }">
        <div class="" style="width:100%;height:100%;display:flex;flex-direction:column;overflow:hidden;">
        <v-row no-gutters v-if="widget || hasError" style="flex:0 0 auto;" :class="{ 'cdb-view-toolrow': !editMode, 'cdb-edit-toolrow': editMode }">
            <v-col cols="12" class="d-flex justify-space-between ">
                <!-- Drag handle is an authoring affordance — hidden in view mode (builder-v1).
                     Default-true prop keeps flag-off / standalone use byte-identical. -->
                <span v-if="editMode" class="mdi mdi-dots-grid widget-drag-handle cdb-edit-chrome" style="cursor: grab;"></span>
                <span v-else></span>

                <div class="d-flex align-center cdb-view-tools">
                <!-- Frozen "reference" badge (decision 3): this source is beyond the
                     self-excluding cap, so it shows its own selection on the other charts but
                     does not itself re-query. Flat outlined chip, no accent stripe. -->
                <v-chip v-if="frozen" x-small label outlined class="mr-2"
                    title="Reference chart: its selection filters the others, but it keeps its own full data. Clear a selection to make it interactive again.">
                    <v-icon x-small left>mdi-pin-outline</v-icon>Reference
                </v-chip>
                <!-- Widget-level filters -->
                <v-btn v-if="parsedSqlViewConfig" small icon elevation="0" @click="toggleFilterPanel($event)">
                  <v-badge :value="activeFilterCount > 0" :content="String(activeFilterCount)"
                    color="primary" overlap offset-x="8" offset-y="8">
                    <v-icon small :color="activeFilterCount > 0 ? 'primary' : ''">mdi-filter</v-icon>
                  </v-badge>
                </v-btn>

                <v-btn small icon elevation="0" @click="refresh" :loading="loading">
                    <v-icon small>mdi-refresh</v-icon>
                </v-btn>
                <!-- Widget-level "Isolate in 3D": charts use their rendered/property colours;
                     Number/KPI widgets use one resolved aggregate status fill when available. -->
                <v-btn v-if="hasConfiguredSpatialAction" small icon elevation="0" @click="emitIsolate()"
                    title="Isolate all available locations in 3D" aria-label="Isolate all available locations in 3D">
                    <v-icon small>mdi-cube</v-icon>
                </v-btn>
                <!-- menu start — authoring kebab (update/duplicate/delete/create-chart):
                     hidden in view mode (builder-v1); refresh + filter stay above. -->
                <v-menu v-if="editMode" dense offset-x right :key="key">
                    <template #activator="{ attrs, on }">
                        <v-btn small icon elevation="0" v-bind="attrs" v-on="on" class="cdb-touch-target cdb-edit-chrome">
                            <i class="fa-solid fa-bars" style="font-size: 16px;"></i>
                        </v-btn>
                    </template>
                    <v-list class="more-options-menu">

                        <v-list-item @click="update(message)" dense>
                            <v-list-item-icon class="mr-2 justify-center">
                                <v-icon small>fas fa-pen</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>Update</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item v-if="isDataGridWidget" @click="createChartFromGrid" dense>
                            <v-list-item-icon class="mr-2 justify-center">
                                <v-icon small>mdi-chart-bar</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>Create chart from grid</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item @click="duplicate(message.id)" dense>
                            <v-list-item-icon class="mr-2 justify-center">
                                <v-icon small>fas fa-clone</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>Duplicate</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item @click="del(message.id)" dense>
                            <v-list-item-icon class="mr-2 justify-center">
                                <v-icon small color="error">fas fa-trash-alt</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title class="error--text">Delete</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-menu>
                <!-- menu end -->
                </div>

            </v-col>

        </v-row>

        <div v-if="message" style="flex:1 1 0;min-height:0;width:100%;overflow:hidden;">
            <!-- The layout manifest already knows the widget type before its data query
                 returns, so first paint can use the correct chart/table/KPI anatomy. -->
            <dashboard-widget-skeleton
                v-if="loading && !widget"
                :widget-type="message && message.messageType"
                :builder-header="true" />
            <!-- Register not configured (AVA-602): a Facility Group register with rows but no
                 Shared/Separated mode. Calm empty-state, not an error — an administrator has to act,
                 so there is no Retry here; the toolrow refresh stays available. -->
            <div v-else-if="configurationRequired" class="cdb-config-required" role="status">
                <v-icon size="32" class="cdb-config-required__icon">mdi-table-cog</v-icon>
                <span class="cdb-config-required__title">{{ notConfiguredCopy.title }}</span>
                <span class="cdb-config-required__text">{{ notConfiguredCopy.message }}</span>
            </div>
            <!-- Error state -->
            <div v-else-if="hasError && !widget" class="d-flex flex-column align-center justify-center" style="height:100%;opacity:.7;">
                <v-icon color="error" size="32">mdi-alert-circle-outline</v-icon>
                <span class="mt-2 text-caption">Failed to load widget</span>
                <!-- UXB-4: labeled inline retry. -->
                <v-btn x-small outlined class="mt-2" :loading="loading" @click="refresh">Retry</v-btn>
            </div>
            <dashboard-widget v-else-if="widget" ref="dashboardWidget" :message="widget"
                :library-schemas="librarySchemas"
                :isolationClaim="isolationClaim"
                :isClaimCurrent="isClaimCurrent"
                :claimIsolation="claimIsolation"
                :filterSessionId="filterSessionId"
                :globalFilters="globalFilters"
                :selectionFilters="selectionFilters"
                :frozen="frozen"
                :widgetFilters="widgetFilterPayload"
                :builderHeader="true"
                :dashboardGridStyle="isSqlDataGridWidget"
                :activeFilterCount="activeFilterCount"
                :dashboardFilterCount="dashboardFilterCount"
                :refreshing="loading"
                :refresh-error="hasError"
                :selectedLabels="selectedLabels"
                :sessionEmptyMatch="sessionEmptyMatch"
                :crossFilterActive="crossFilterActive"
                :listDrillable="listDrillable"
                :listDrillBusy="listDrillBusy"
                @listDrill="onListDrill"
                @openViewer="onOpenViewer"
                @spatialCapability="gridSpatialCapability = $event"
                @openRowDetails="onOpenRowDetails"
                @openGridFilters="toggleFilterPanel"
                @refreshGrid="refresh"
                @clearGridFilters="clearWidgetFiltersFromGrid"
                @clearCrossFilter="$emit('clearCrossFilter')"
                @selectionFilterRejected="$emit('selectionFilterRejected', $event)"
                @openDashboardFilters="$emit('openDashboardFilters')"
                @contextmenu="openExplanationContextMenu"
                @legendFilter="$emit('legendFilter', $event)">
                <template #title-actions>
                    <v-tooltip v-if="canExplain" bottom>
                        <template #activator="{ attrs, on }">
                            <v-btn icon x-small class="cdb-explain-menu-trigger"
                                aria-label="About this number" v-bind="attrs" v-on="on"
                                @click="openExplanation()">
                                <v-icon small>mdi-information-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ explanationTooltip }}</span>
                    </v-tooltip>
                </template>
            </dashboard-widget>
        </div>
    </div>
    <edit-chart-message ref="editWidget" @updated="updateConfirmed"></edit-chart-message>
    <edit-sql-view-widget ref="editSqlViewWidget" :library-schemas="librarySchemas" @updated="updateConfirmed"></edit-sql-view-widget>

    <!-- Filter panel — rendered here, teleported to body to escape grid-item stacking context -->
    <div v-if="filterMenuOpen && parsedSqlViewConfig"
      ref="filterPanel"
      class="widget-filter-panel v-application"
      :class="$vuetify.theme.isDark ? 'theme--dark' : 'theme--light'"
      :style="filterPanelStyle">
      <widget-filter-menu
        :sqlViewConfig="parsedSqlViewConfig"
        :columns="filterableColumns"
        :filterSessionId="filterSessionId"
        :messageType="widget && widget.messageType"
        v-model="widgetFilters"
        @apply="applyWidgetFilters"
        @reset="resetWidgetFilters"
        @filterCountChange="activeFilterCount = $event"
        @close="filterMenuOpen = false"
        @dragstart="onFilterDragStart"
        @dragmove="onFilterDragMove"
        @dragend="onFilterDragEnd">
      </widget-filter-menu>
    </div>

    <widget-explanation-drawer
      v-if="explanationOpen"
      :value="explanationOpen"
      :explanation="explanation"
      :loading="explanationLoading || aboutFiltersPending"
      :error="explanationError"
      :scope-mode="explanationScopeMode"
      :selection="selection"
      :widget-title="(widget && widget.title) || (message && message.title) || ''"
      @close="closeExplanation"
      @retry="retryExplanation()"
      @scope-change="changeExplanationScope" />
    </div>
</template>

<script>
import { log, warn, error } from '@/utils/debugLogger';
import { debounce } from 'lodash';
import DashboardWidget from '@/features/Chatbot/components/dashboard-widget.vue'
import { prepareChartData, preparedDatumValues } from '@/features/Dashboard/service/chart-data.service';
import { columnDisplayName } from '@/features/Dashboard/utils/columnLabels';
import DashboardWidgetSkeleton from './DashboardWidgetSkeleton.vue';
import apiService from '../service/dashboard-widgets.service';
import EditChartMessage from '@/features/Chatbot/components/edit-chart-message.vue';
import EditSqlViewWidget from './EditSqlViewWidget.vue';
import WidgetFilterMenu from './WidgetFilterMenu.vue';
import WidgetExplanationDrawer from './WidgetExplanationDrawer.vue';
import { resolveTagLink } from '@/features/Dashboard/utils/tagLink';
import {
    REGISTER_NOT_CONFIGURED_COPY,
    isRegisterConfigurationRequiredError,
} from '@/utils/registerConfigurationError';
import { extractRenderedCategoryColors } from '../utils/chartRenderedColors';
import { MATRIX_TYPES } from '@/features/Dashboard/charts/echartsOptions';
import {
    buildWidgetExplanationRequest,
    createExplanationRequestCoordinator,
    mergeExplanationWidgetFilters,
} from '@/features/Dashboard/utils/widgetExplanation';
import { expectedDrillTotal, listDrillColumn } from '@/features/Dashboard/utils/listDrill';

// Fresh default widget-filter state. A factory (not a shared const) so data() and every
// reset site get their own nested objects instead of aliasing one shared instance.
function defaultWidgetFilters() {
    return {
        propertyFilters: {},
        dateZoom: { column: null, preset: null, customStart: null, customEnd: null },
        topBottom: { mode: null, column: null },
        sort: { mode: null, column: null },
        blanks: { mode: 'include', column: null },
    };
}

export default {
    props: {
        message: {},
        // Active tab's coloring-schema library (registry, COLORING_SCHEMA_REGISTRY.md), forwarded
        // from CustomDashboard to the widget + the per-item edit wizard. Array|null; null = loading
        // or none (see the widget's reference render-gate). type:Array + default:null is null-safe
        // (Vue 2 skips the type check when the value is null and the prop is not required).
        librarySchemas: { type: Array, default: null },
        isolationClaim: { type: Object, default: null },
        isClaimCurrent: { type: Function, default: () => true },
        claimIsolation: { type: Function, default: null },
        filterSessionId: {},
        // Raw global filters ({facilityId, dateFrom, dateTo}) forwarded ONLY while the
        // dashboard's filter session is degraded (creation failed). Null in the healthy
        // path — widgets then rely on filterSessionId. Feeds both the query() path
        // (chart/count/KPI, see getData) and the datagrid path (dashboard-widget →
        // SqlViewDataGrid, which already prefers session and falls back to these).
        globalFilters: {},
        // About may describe the current filters while the data query keeps its session authority.
        aboutGlobalFilters: { type: Object, default: null },
        aboutFiltersPending: { type: Boolean, default: false },
        // TRANSIENT same-table cross-filter predicates (dashboardSelectionScope). Same-table
        // dimensions are exact raw filters, not tag sessions: many rows can share one Master Tag,
        // so a tag intersection is WIDER than the rows the user selected. Merged at payload-build
        // time with the saved/runtime widget filters and NEVER written into filter-panel state —
        // the user's own widget filters must stay exactly what they configured.
        selectionFilters: { type: Array, default: () => [] },
        // Multi-selection freeze (decision 3): true for a widget on a selected table beyond the
        // table session cap. A frozen widget keeps its current data (skips re-query) and
        // shows a "reference" badge, replacing the old single-source skip-guard.
        frozen: { type: Boolean, default: false },
        // This widget's own selection entry from the map ({ filterColumn, values: [...] }) or
        // null. Drives the chart's selectedLabels highlight; forwarded to dashboard-widget.
        selection: { type: Object, default: null },
        // True when this widget's assigned session was created with ZERO tags — an empty-match
        // (the combined selection matched nothing), NOT a stale session. On this flag the
        // widget renders empty and does not renew (decision 10). Forwarded to the datagrid too.
        sessionEmptyMatch: { type: Boolean, default: false },
        // True when ANY chart selection is active on the tab. Lets the chart show its
        // "emptied by another chart's selection" empty state + a clear affordance (decision 12).
        crossFilterActive: { type: Boolean, default: false },
        dashboardFilterCount: { type: Number, default: 0 },
        // Edit-mode gate for authoring affordances (drag handle + kebab). Default true so
        // flag-off and any standalone use render exactly as before builder-v1.
        editMode: { type: Boolean, default: true },
    },
    components: {
        DashboardWidget,
        DashboardWidgetSkeleton,
        EditChartMessage,
        EditSqlViewWidget,
        WidgetFilterMenu,
        WidgetExplanationDrawer,
    },
    data() {
        return {
            loading: false,
            hasError: false,
            configurationRequired: false,
            notConfiguredCopy: REGISTER_NOT_CONFIGURED_COPY,
            widget: null,
            key:'',
            filterMenuOpen: false,
            activeFilterCount: 0,
            filterPanelTop: 0,
            filterPanelLeft: 0,
            filterPanelX: 0,
            filterPanelY: 0,
            isDraggingFilter: false,
            dragInitialX: 0,
            dragInitialY: 0,
            widgetFilters: defaultWidgetFilters(),
            explanationOpen: false,
            explanationScopeMode: 'all',
            explanation: null,
            explanationLoading: false,
            explanationError: false,
            gridSpatialCapability: { status: 'loading' },
            // KPI drill to list: true while the parent reads the ids behind the number.
            listDrillBusy: false,
        }
    },
    computed: {
        explanationTooltip() {
            if (this.isSqlDataGridWidget && this.gridSpatialCapability?.status === 'unavailable') {
                return this.gridSpatialCapability.reason === 'noLocations'
                    ? 'No records in this view have an available 3D location.'
                    : 'This data is not linked to the 3D model.';
            }
            return 'About this number';
        },
        // The selected values (server-side, null-preserving) forwarded to the chart so it can
        // highlight this widget's own current selection. Empty when there is no selection.
        selectedLabels() {
            const values = this.selection && this.selection.values;
            return Array.isArray(values) ? values.map(v => v.value) : [];
        },
        parsedSqlViewConfig() {
            if (!this.widget?.sqlViewConfig) return null;
            try {
                return typeof this.widget.sqlViewConfig === 'string'
                    ? JSON.parse(this.widget.sqlViewConfig)
                    : this.widget.sqlViewConfig;
            } catch (err) {
                return null;
            }
        },
        /** The column a KPI drill lists (sqlViewConfig.listDrill), or null when it does not drill. */
        listDrillColumn() {
            return listDrillColumn(this.parsedSqlViewConfig, this.widget?.messageType);
        },
        /** The number opens its list in view mode only; in edit mode a click belongs to the layout. */
        listDrillable() {
            return !this.editMode && !!this.listDrillColumn;
        },
        /** True for grid-style SQL-view widgets that can seed a chart from their source */
        isDataGridWidget() {
            return !!this.parsedSqlViewConfig &&
                (this.widget?.messageType === 'datagrid' || this.widget?.messageType === 'table');
        },
        /** True only for the server-backed AG Grid widget rendered on Custom Dashboards. */
        isSqlDataGridWidget() {
            return !!this.parsedSqlViewConfig && this.widget?.messageType === 'datagrid';
        },
        /** Show the per-chart "Isolate in 3D" button only when the same presence-aware tag-link
         *  resolver used by the grid admits the source. Explicit server `tagLink: null` fails
         *  closed; saved widgets from before that descriptor keep the narrow legacy fallback. */
        hasConfiguredSpatialAction() {
            const cfg = this.parsedSqlViewConfig;
            if (!cfg || !cfg.link3D || this.isDataGridWidget || this.frozen) return false;
            const isKpi = this.widget?.messageType === 'count';
            const hasGroupBy = !!(cfg.aggregation && Array.isArray(cfg.aggregation.groupBy) && cfg.aggregation.groupBy.length);
            if (MATRIX_TYPES.includes(this.widget?.messageType)) {
                const valueColors = cfg.coloring && cfg.coloring.valueColors;
                const hasExplicitPropertyColors = cfg.highlightColorSource === 'property'
                    && !!cfg.coloring?.column
                    && !!valueColors
                    && Object.keys(valueColors).length > 0;
                if (!hasExplicitPropertyColors) return false;
            }
            const availableColumns = Array.isArray(cfg.columns) && cfg.columns.length
                ? cfg.columns
                : Object.keys(this.widget?.content?.[0] || {});
            const serverTagLink = Object.prototype.hasOwnProperty.call(cfg, 'tagLink')
                ? cfg.tagLink
                : undefined;
            const tagLink = resolveTagLink({
                serverTagLink,
                legacyTagColumn: cfg.tagColumn,
                tableName: cfg.tableName,
                availableColumns,
            });
            return (isKpi || hasGroupBy) && !!tagLink;
        },
        canExplain() {
            return !!this.message?.id && !!this.parsedSqlViewConfig;
        },
        filterableColumns() {
            const config = this.parsedSqlViewConfig;
            if (!config) return [];

            // Primary: use columnTypes from sqlViewConfig
            if (config.columnTypes && Object.keys(config.columnTypes).length > 0) {
                return Object.entries(config.columnTypes).map(([name, dataType]) => ({
                    name,
                    dataType,
                }));
            }

            // Fallback: infer columns from widget content data
            if (this.widget?.content && Array.isArray(this.widget.content) && this.widget.content.length > 0) {
                const sample = this.widget.content[0];
                return Object.keys(sample).map(name => ({
                    name,
                    dataType: this.inferDataType(sample[name]),
                }));
            }

            // Fallback: use columns list from config without type info
            if (config.columns && config.columns.length > 0) {
                return config.columns.map(name => ({ name, dataType: 'nvarchar' }));
            }

            return [];
        },
        hasWidgetFilters() {
            const f = this.widgetFilters;
            if (Object.keys(f.propertyFilters).some(k => f.propertyFilters[k]?.length > 0)) return true;
            if (f.dateZoom.preset) return true;
            if (f.topBottom.mode) return true;
            if (f.sort.mode) return true;
            if (f.blanks.mode === 'exclude') return true;
            return false;
        },
        widgetFilterPayload() {
            if (!this.hasWidgetFilters) return null;
            return this.buildWidgetFilterPayload();
        },
        // Read from the layout entity (message), not `widget`, so the answer is correct
        // even while the initial load is in flight or has failed.
        isDatagridMessage() {
            return this.message?.messageType === 'datagrid';
        },
        filterPanelStyle() {
            return {
                top: this.filterPanelTop + 'px',
                left: this.filterPanelLeft + 'px',
                transform: `translate3d(${this.filterPanelX}px, ${this.filterPanelY}px, 0)`,
                transition: this.isDraggingFilter ? 'none' : 'transform 0.15s ease',
            };
        },
    },
    mounted() {
        if (!this.message) return;
        this.getData();
    },
    methods: {
        getData(explicitWidgetFilters) {
            // Frozen reference widget (its table is beyond the table session cap): keep the
            // current data instead of re-querying, because without an external-only session the
            // re-query would fall back to the FULL session and widen the widget through that
            // table's own tag intersection.
            //
            // Only when there IS current data. The cap now covers every widget on a capped table,
            // not just the source chart the user clicked, so a widget can mount already frozen —
            // and skipping its first load would leave a permanently blank tile with nothing to
            // preserve. Freezing protects existing rows; it must not prevent ever having any.
            if (this.frozen && this.widget) return;

            // Supersede any in-flight query for THIS widget: abort it on the wire (not just ignore
            // its late response) so a rapid re-filter stops wasting server / rate-limiter budget.
            // Pairs with the _requestSeq latest-wins guard below (which still covers a response that
            // resolved before its abort landed).
            if (this._abortController) this._abortController.abort();
            this._abortController = new AbortController();
            const signal = this._abortController.signal;

            // Latest-wins: a stale query response must not overwrite a newer render.
            this._requestSeq = (this._requestSeq || 0) + 1;
            const seq = this._requestSeq;

            this.loading = true;
            const queryOpts = { filterSessionId: this.filterSessionId, signal };
            // Degraded mode: forward the raw global filters so the widget still filters
            // (via the server raw path) instead of returning everything. Null when healthy,
            // so only filterSessionId is sent — the two are mutually exclusive by design
            // (fallbackGlobalFilters is null unless the session failed), matching the
            // prefer-session/fallback-raw split the datagrid path uses.
            if (this.globalFilters) queryOpts.globalFilters = this.globalFilters;

            // Use explicit filters if passed (from applyWidgetFilters), otherwise check current state
            const filtersToUse = explicitWidgetFilters || (this.hasWidgetFilters ? this.widgetFilters : null);
            if (filtersToUse) {
                queryOpts.widgetFilters = this.buildWidgetFilterPayloadFrom(filtersToUse);
            }
            // Same-table cross-filter predicates ride in the SAME widget-filter payload the render
            // path already sends — merged here, at payload-build time, so the user's own filter
            // state is untouched and a cleared selection simply stops contributing. This is the
            // channel that makes a KPI on the selected table report the chart bucket's count
            // instead of every row under a matching Master Tag.
            queryOpts.widgetFilters = mergeExplanationWidgetFilters(
                queryOpts.widgetFilters,
                this.selectionFilters,
            );
            if (!Object.keys(queryOpts.widgetFilters).length) delete queryOpts.widgetFilters;
            apiService.query(this.message?.id, queryOpts).then(res => {
                if (seq !== this._requestSeq) return;
                try {
                    var data = res.data;
                    data.content = JSON.parse(data.content);
                    // API may return {Content:[...], Series:[], TotalCount:N} wrapper — unwrap to flat array
                    if (data.content && !Array.isArray(data.content) && Array.isArray(data.content.Content)) {
                        data.content = data.content.Content;
                    }
                    if(data.legend)
                    data.legend = JSON.parse(data.legend);
                    // Empty-match vs stale session (decision 10). The api can't tell a 0-tag
                    // AND-intersection from a purged session at apply time — both return
                    // unfiltered rows + 'session-expired'. When THIS widget's session was created
                    // with 0 tags (sessionEmptyMatch), the combined selection matched nothing:
                    // force empty content BEFORE assigning so the widget never paints the
                    // unfiltered rows the api returned. Set before this.widget = data.
                    const skippedForSession = data.filtersApplied === false && data.filterSkipReason === 'session-expired';
                    if (skippedForSession && this.sessionEmptyMatch) {
                        data.content = [];
                    }
                    this.widget = data;
                    this.hasError = false;
                    this.configurationRequired = false;
                    // B2: the API bubbles filtersApplied=false + filterSkipReason on the VM (top-level,
                    // siblings of content) when a source has no tag column; dashboard-widget reads them
                    // off `message` to show the skip note. Log the skip so B4 can trace it on the wire.
                    if (data.filtersApplied === false) {
                        log('[DashboardItem] dashboard filters skipped for widget', this.message?.id, data.filterSkipReason || '');
                        // Session lifecycle (decision 9): a genuinely purged/expired sid returns
                        // this reason instead of silent empty data. Renew ONLY when this is not an
                        // empty-match (local tagCount > 0) — a 0-tag session must not renew or it
                        // would be recreated forever. Bubble up; CustomDashboard renews once.
                        if (skippedForSession && !this.sessionEmptyMatch) {
                            this.$emit('sessionExpired');
                        }
                    }
                } catch (err) {
                    log('Error parsing dashboard widget data', err);
                    this.configurationRequired = false;
                    this.hasError = true;
                }
            }).catch(err => {
                // A superseded / teardown request is aborted on purpose — never a load failure, so
                // don't flip the widget to an error card. (Its seq is usually already stale too.)
                if (err && (err.code === 'ERR_CANCELED' || err.name === 'CanceledError' || err.name === 'AbortError' || err.__CANCEL__)) return;
                if (seq !== this._requestSeq) return;
                // hasError stays true for this case too, so the toolrow refresh and the
                // filter-change self-heal keep working; the template shows the calm state first.
                this.configurationRequired = isRegisterConfigurationRequiredError(err);
                if (this.configurationRequired) {
                    log('DashboardItem', 'widget source register needs an assignment mode', this.message?.id);
                } else {
                    error('DashboardItem', 'Failed to load widget', err);
                }
                this.hasError = true;
            }).finally(() => {
                if (seq === this._requestSeq) this.loading = false;
            })
        },
        openExplanation(scopeOverride = null) {
            if (!this.canExplain) return;
            const hasOwnSelection = !!this.selection?.filterColumn
                && Array.isArray(this.selection?.values)
                && this.selection.values.length > 0;
            this.explanationScopeMode = scopeOverride || (hasOwnSelection ? 'selected' : 'all');
            this.explanation = null;
            this.explanationError = false;
            this.explanationOpen = true;
            this.loadExplanation();
        },
        openExplanationContextMenu(event) {
            if (!this.canExplain || !event) return;
            const target = event.target;
            if (target?.closest?.('input, textarea, select, [contenteditable="true"]')) return;
            event.preventDefault();
            this.openExplanation();
        },
        loadExplanation() {
            if (!this.explanationOpen || !this.canExplain || this.aboutFiltersPending) return;
            const gridScope = this.isSqlDataGridWidget
                ? this.$refs.dashboardWidget?.getGridExplanationScope?.()
                : null;
            if (this.isSqlDataGridWidget && !gridScope) {
                this.explanationLoading = false;
                this.explanationError = true;
                return;
            }
            const widgetFilters = this.hasWidgetFilters ? this.buildWidgetFilterPayload() : {};
            if (gridScope?.runtimeFilters?.length) {
                widgetFilters.filters = [
                    ...(widgetFilters.filters || []),
                    ...gridScope.runtimeFilters,
                ];
            }
            if (gridScope?.sort) widgetFilters.sort = gridScope.sort;
            if (gridScope?.topBottom) widgetFilters.topBottom = gridScope.topBottom;
            const request = this._explanationCoordinator.begin();
            const payload = buildWidgetExplanationRequest({
                filterSessionId: this.filterSessionId,
                globalFilters: this.aboutGlobalFilters || this.globalFilters,
                widgetFilters: Object.keys(widgetFilters).length ? widgetFilters : null,
                selectionFilters: this.selectionFilters,
                selection: this.selection,
                selectedScope: this.explanationScopeMode === 'selected',
                sourceSearch: gridScope?.sourceSearch,
            });
            this.explanationLoading = true;
            this.explanationError = false;
            apiService.explain(this.message.id, payload, { signal: request.signal }).then(res => {
                if (!this._explanationCoordinator.isCurrent(request.token)) return;
                this.explanation = res.data;
            }).catch(err => {
                if (err && (err.code === 'ERR_CANCELED' || err.name === 'CanceledError' || err.name === 'AbortError' || err.__CANCEL__)) return;
                if (!this._explanationCoordinator.isCurrent(request.token)) return;
                error('DashboardItem', 'Failed to load widget explanation', err);
                this.explanationError = true;
            }).finally(() => {
                if (this._explanationCoordinator.isCurrent(request.token)) this.explanationLoading = false;
            });
        },
        retryExplanation() {
            if (this.explanation?.unsupportedCode === 'currentFiltersUnavailable' && this.filterSessionId) {
                this.$emit('sessionExpired');
            }
            this.loadExplanation();
        },
        closeExplanation() {
            this._explanationCoordinator.cancel();
            this.explanationOpen = false;
            this.explanationLoading = false;
            this.explanationError = false;
            this.explanation = null;
        },
        invalidateExplanationScope() {
            this._explanationCoordinator.cancel();
            this.explanation = null;
            this.explanationError = false;
            if (this.explanationOpen) this.debouncedReloadExplanation();
        },
        changeExplanationScope(scope) {
            const nextScope = scope === 'selected' ? 'selected' : 'all';
            if (nextScope === this.explanationScopeMode) return;
            this._explanationCoordinator.cancel();
            this.explanationScopeMode = nextScope;
            this.explanation = null;
            this.explanationError = false;
            this.loadExplanation();
        },
        refresh() {
            this.getData();
        },
        duplicate() {
            this.$emit('duplicate', this.widget?.id || this.message?.id);
        },
        del() {
            this.$emit('del', this.widget?.id || this.message?.id);
        },
        /** Ask the dashboard to open the Add-Widget wizard seeded with this grid's source */
        createChartFromGrid() {
            const cfg = this.parsedSqlViewConfig;
            if (!cfg || !cfg.tableName) return;
            this.$emit('createChartFromGrid', {
                tableName: cfg.tableName,
                tableDisplayName: cfg.tableDisplayName || null,
                columns: Array.isArray(cfg.columns) ? [...cfg.columns] : [],
                columnTypes: { ...(cfg.columnTypes || {}) },
                columnDisplayNames: { ...(cfg.columnDisplayNames || {}) },
                // #9 provenance: the wizard persists this inside sqlViewConfig so #10 can trace
                // a chart back to the grid it was created from. Consumed defensively downstream.
                createdFromWidgetId: this.message?.id,
                ...(Object.prototype.hasOwnProperty.call(cfg, 'tagLink') ? { tagLink: cfg.tagLink } : {}),
            });
        },
        onOpenViewer(operation = null) {
            if (operation) this.$emit('openViewer', this.message?.id, operation);
            else this.$emit('openViewer', this.message?.id);
        },
        onOpenRowDetails(details) {
            if (!details?.row) return;
            this.$emit('openRowDetails', {
                ...details,
                widgetId: this.message?.id,
            });
        },
        isolateDetailsRow(row) {
            this.$refs.dashboardWidget?.isolateDetailsRow?.(row);
        },
        /** Isolate every matching row for this chart/KPI through the existing exact-focus path. */
        emitIsolate() {
            const cfg = this.parsedSqlViewConfig;
            if (!cfg || !this.hasConfiguredSpatialAction) return;
            const isKpi = this.widget?.messageType === 'count';
            const isMatrixType = MATRIX_TYPES.includes(this.widget?.messageType);
            const prepared = (isMatrixType || isKpi)
                ? null
                : prepareChartData(this.widget?.content || [], this.widget?.messageType);
            const filterColumn = (cfg.aggregation && cfg.aggregation.groupBy && cfg.aggregation.groupBy[0]) || null;
            this.$store.commit('SET_VIEWER_TYPE', '3d');
            this.$emit('isolate-widget', {
                widgetId: this.message?.id,
                tableName: cfg.tableName,
                filterColumn,
                tagColumn: cfg.tagColumn || null,
                tagLink: cfg.tagLink,
                labelDomain: Array.isArray(prepared?.labels) ? prepared.labels : [],
                // The chart's own number per category, read from the SAME prepared pass as the label
                // domain (a matrix widget prepares nothing and ships none). This button's legend
                // reports the dual count exactly as a slice-click isolate does, instead of a bare
                // painted count that reads as if it contradicted the chart.
                datumValues: isKpi ? null : preparedDatumValues(prepared),
                // How this widget NAMES its group-by column and itself. The legend is a caption for
                // this chart, so it must head with the chart's vocabulary, never the raw identifier.
                filterColumnLabel: filterColumn ? columnDisplayName(filterColumn, cfg.columnDisplayNames) : null,
                widgetTitle: (this.widget && this.widget.title) || (this.message && this.message.title) || null,
                filters: [
                    ...((cfg.filters) || []),
                    ...((this.widgetFilterPayload && Array.isArray(this.widgetFilterPayload.filters)) ? this.widgetFilterPayload.filters : []),
                ],
                coloring: cfg.coloring || null,
                coloringSource: cfg.coloringSource || null,
                schemaSelection: cfg.schemaSelection || null,
                renderedValueColors: this.renderedChartCategoryColors(),
                // Which of the two colour definitions this widget declared as the winner. Absent on
                // widgets saved before that control existed — the parent treats absent as "behave
                // exactly as before".
                //
                // `poiSource` is deliberately NOT forwarded any more. It named the column a CLIENT
                // resolver was to walk; which point of interest a row owns is now the source's own
                // server-side `spatial` contract, so passing the widget's opinion alongside it would
                // be a second answer to a question the catalogue already settled.
                highlightColorSource: cfg.highlightColorSource || null,
                // Presentation metadata stays atomic and client-only. With no resolved status this
                // is null, so CustomDashboard retains the existing property-colour/plain fallback.
                presentation: isKpi ? this.kpiIsolationPresentation() : null,
            });
        },
        kpiIsolationPresentation() {
            const presentation = this.$refs.dashboardWidget?.kpiSpatialPresentation;
            return presentation && typeof presentation === 'object'
                ? { ...presentation }
                : null;
        },
        renderedChartCategoryColors() {
            return extractRenderedCategoryColors(this.$refs.dashboardWidget?.echartsOption);
        },
        /** Called by parent after 3D viewer is loaded */
        isolateSelectedTag(operation = null, spatialFocusSessionOverride = null, onFallbackResolved = null) {
            const grid = this.$refs.dashboardWidget?.$refs.sqlViewDataGrid;
            grid?.setSpatialFocusSessionOverride?.(spatialFocusSessionOverride, onFallbackResolved);
            grid?.isolateSelectedTag(operation);
        },
        replaySpatialFocusForFallback(spatialFocusSessionOverride) {
            const grid = this.$refs.dashboardWidget?.$refs.sqlViewDataGrid;
            grid?.setSpatialFocusSessionOverride?.(spatialFocusSessionOverride);
            return grid?.replaySpatialFocusForFallback?.() || false;
        },
        prepareIsolationOwnershipChange() {
            this.$refs.dashboardWidget?.prepareIsolationOwnershipChange?.();
        },
        cancelPendingIsolation(operation = null) {
            this.$refs.dashboardWidget?.cancelPendingIsolation?.(operation);
        },
        update() {
            var payload=structuredClone(this.widget);
            if (payload.sqlViewConfig) {
                this.$refs.editSqlViewWidget.open(payload);
            } else {
                this.$refs.editWidget.open(payload, { editMode: true });
            }
        },
        updateConfirmed(widget, settle) {
            // Editors without a settle callback (the chart-message editor) keep the optimistic update.
            if (typeof settle !== 'function') {
                this.widget = widget;
                this.$emit('update', widget);
                this.$refs.editWidget.close();
                return;
            }
            // The SQL-view wizard: adopt the edited config only once the server accepted it, and let
            // the wizard show the server's errors when it did not.
            if (!this.$listeners.update) {
                this.widget = widget;
                settle();
                return;
            }
            this.$emit('update', widget, (err) => {
                if (!err) this.widget = widget;
                settle(err);
            });
        },
        buildWidgetFilterPayload() {
            return this.buildWidgetFilterPayloadFrom(this.widgetFilters);
        },
        /**
         * KPI drill to list: hand CustomDashboard everything that scopes this number - the saved
         * filters, this widget's own runtime filters, the same-table cross-filter predicates, and
         * the dashboard filter session (or the raw fallback filters while it is degraded) - so the
         * list it opens holds exactly the records the number counts. CustomDashboard owns the
         * filter-session state and the table descriptors, so it runs the drill and calls back.
         * @param {{ value?: * }} [event] - the number the widget shows
         */
        onListDrill(event) {
            const column = this.listDrillColumn;
            const config = this.parsedSqlViewConfig;
            if (!column || !config || this.listDrillBusy) return;
            this.listDrillBusy = true;
            this.$emit('listDrill', {
                widgetId: this.message?.id,
                tableName: config.tableName,
                column,
                savedFilters: Array.isArray(config.filters) ? config.filters : [],
                runtimeFilters: this.hasWidgetFilters ? this.buildWidgetFilterPayload().filters : [],
                selectionFilters: this.selectionFilters,
                filterSessionId: this.filterSessionId,
                globalFilters: this.globalFilters,
                expectedTotal: expectedDrillTotal(config, column, event && event.value),
                // The number itself was computed without the dashboard filters (the tile shows
                // "Dashboard filters not applied"); a zero-tag empty match is not that case.
                numberIgnoresFilters: this.widget?.filtersApplied === false && !this.sessionEmptyMatch,
            }, () => { this.listDrillBusy = false; });
        },
        buildWidgetFilterPayloadFrom(source) {
            const filters = [];
            const f = source;

            // Property filters → IN clauses
            for (const [column, values] of Object.entries(f.propertyFilters)) {
                if (values && values.length > 0) {
                    filters.push({ column, operator: 'in', value: values });
                }
            }

            // Date zoom → between/gte filter
            if (f.dateZoom.column && f.dateZoom.preset) {
                const range = this.resolveDateRange(f.dateZoom);
                if (range) {
                    filters.push({ column: f.dateZoom.column, operator: 'between', value: [range.start, range.end] });
                }
            }

            // Blanks → is_not_null filter
            if (f.blanks.mode === 'exclude') {
                // Apply to all filterable columns if no specific column chosen
                const cols = f.blanks.column ? [f.blanks.column] : this.filterableColumns.map(c => c.name);
                cols.forEach(col => {
                    filters.push({ column: col, operator: 'is_not_null', value: null });
                });
            }

            const payload = { filters };

            // Sort
            if (f.sort.mode && f.sort.column) {
                const dirMap = { highToLow: 'desc', lowToHigh: 'asc', aToZ: 'asc', zToA: 'desc', newest: 'desc', oldest: 'asc' };
                payload.sort = { column: f.sort.column, direction: dirMap[f.sort.mode] || 'asc' };
            }

            // Top/Bottom
            if (f.topBottom.mode && f.topBottom.column) {
                const match = f.topBottom.mode.match(/^(top|bottom)(\d+)$/);
                if (match) {
                    payload.topBottom = { mode: match[1], count: parseInt(match[2]), column: f.topBottom.column };
                }
            }

            return payload;
        },
        resolveDateRange(dateZoom) {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const toISO = d => d.toISOString().split('T')[0];
            switch (dateZoom.preset) {
                case 'today':
                    return { start: toISO(today), end: toISO(today) };
                case 'thisWeek': {
                    const day = today.getDay();
                    const monday = new Date(today);
                    monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
                    const sunday = new Date(monday);
                    sunday.setDate(monday.getDate() + 6);
                    return { start: toISO(monday), end: toISO(sunday) };
                }
                case 'thisMonth': {
                    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                    return { start: toISO(firstDay), end: toISO(lastDay) };
                }
                case 'last30': {
                    const past = new Date(today);
                    past.setDate(today.getDate() - 30);
                    return { start: toISO(past), end: toISO(today) };
                }
                case 'last90': {
                    const past = new Date(today);
                    past.setDate(today.getDate() - 90);
                    return { start: toISO(past), end: toISO(today) };
                }
                case 'custom':
                    if (dateZoom.customStart && dateZoom.customEnd) {
                        return { start: dateZoom.customStart, end: dateZoom.customEnd };
                    }
                    return null;
                default:
                    return null;
            }
        },
        inferDataType(value) {
            if (value === null || value === undefined) return 'nvarchar';
            if (typeof value === 'number') return 'decimal';
            if (typeof value === 'boolean') return 'bit';
            // Check for ISO date string patterns
            if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return 'datetime';
            return 'nvarchar';
        },
        toggleFilterPanel(e) {
            this.filterMenuOpen = !this.filterMenuOpen;
            if (this.filterMenuOpen) {
                const btn = e.currentTarget;
                const rect = btn.getBoundingClientRect();
                this.filterPanelTop = rect.bottom + 4;
                this.filterPanelLeft = Math.max(8, rect.right - 400);
                this.filterPanelX = 0;
                this.filterPanelY = 0;
            }
        },
        onFilterDragStart(e) {
            this.isDraggingFilter = true;
            this.dragInitialX = e.clientX - this.filterPanelX;
            this.dragInitialY = e.clientY - this.filterPanelY;
        },
        onFilterDragMove(e) {
            if (!this.isDraggingFilter) return;
            this.filterPanelX = e.clientX - this.dragInitialX;
            this.filterPanelY = e.clientY - this.dragInitialY;
        },
        onFilterDragEnd() {
            this.isDraggingFilter = false;
        },
        applyWidgetFilters(filtersSnapshot) {
            this.filterMenuOpen = false;
            // Use the snapshot directly — don't rely on v-model timing
            if (filtersSnapshot) {
                this.widgetFilters = filtersSnapshot;
            }
            this.invalidateExplanationScope();
            this.getData(this.widgetFilters);
        },
        resetWidgetFilters(filtersSnapshot) {
            this.filterMenuOpen = false;
            this.activeFilterCount = 0;
            if (filtersSnapshot) {
                this.widgetFilters = filtersSnapshot;
            }
            this.invalidateExplanationScope();
            this.getData();
        },
        clearWidgetFiltersFromGrid() {
            this.filterMenuOpen = false;
            this.activeFilterCount = 0;
            this.widgetFilters = defaultWidgetFilters();
            this.invalidateExplanationScope();
            this.getData();
        },
    },
    created() {
        this._explanationCoordinator = createExplanationRequestCoordinator();
        // Create per-instance debounced handler so each widget has its own timer.
        // hasError is included so a widget whose initial load failed re-queries on the next
        // session/filter change (self-heal) instead of staying dead until manual Retry; a
        // first load still in flight (neither widget nor error yet) keeps coalescing.
        this.debouncedGetData = debounce(function () {
            if (this.widget || this.hasError) {
                this.getData();
            }
        }.bind(this), 150);
        this.debouncedReloadExplanation = debounce(function () {
            if (this.explanationOpen) this.loadExplanation();
        }.bind(this), 100);
    },
    watch: {
        // Datagrid widgets skip these re-queries: the grid receives filterSessionId /
        // sessionEmptyMatch / globalFilters as direct props and refetches its own rows via
        // tables/data, so re-querying the widget VM here only duplicated that refresh (and
        // the replaced VM tripped the grid's message.content watcher into a third fetch).
        filterSessionId() {
            this.invalidateExplanationScope();
            if (this.isDatagridMessage) return;
            this.debouncedGetData();
        },
        // A widget whose table drops back under the table session cap (frozen true → false) has a
        // live external-only sid again, so re-query to reflect the other tables' selections.
        frozen(isFrozen) {
            if (!isFrozen && !this.isDatagridMessage) this.debouncedGetData();
        },
        // Same-table selections change this widget's exact row scope with no session change at
        // all, so it needs its own re-query trigger. Datagrids refetch from the direct prop.
        // Debounced + coalesced with the filterSessionId watcher: one selection commit can move
        // both channels at once and must still fire a single fetch.
        selectionFilters() {
            this.invalidateExplanationScope();
            if (this.isDatagridMessage) return;
            this.debouncedGetData();
        },
        // Re-query when the dashboard flips between healthy (session) and degraded (raw
        // globalFilters), or when the raw filters themselves change while degraded. Reference
        // change only (the parent recomputes fallbackGlobalFilters), debounced + coalesced
        // with the filterSessionId watcher so a flip fires a single fetch, not two.
        globalFilters() {
            this.invalidateExplanationScope();
            if (this.isDatagridMessage) return;
            this.debouncedGetData();
        },
        aboutGlobalFilters: {
            deep: true,
            handler() { this.invalidateExplanationScope(); },
        },
        aboutFiltersPending() { this.invalidateExplanationScope(); },
        selection: {
            deep: true,
            handler() {
                this.invalidateExplanationScope();
                if (this.explanationScopeMode === 'selected' && !this.selection?.values?.length) {
                    this.explanationScopeMode = 'all';
                }
            },
        },
        filterMenuOpen(open) {
            // Teleport filter panel to body to escape grid-item stacking context
            this.$nextTick(() => {
                const el = this.$refs.filterPanel;
                if (!el) return;
                if (open) {
                    document.body.appendChild(el);
                }
            });
        },
    },
    beforeDestroy() {
        // Abort any in-flight widget query so an unmounted widget stops holding a connection.
        if (this._abortController) this._abortController.abort();
        this._explanationCoordinator?.cancel();
        this.debouncedReloadExplanation?.cancel?.();
        this.debouncedGetData?.cancel?.();
        // Clean up teleported element if still on body
        const el = this.$refs.filterPanel;
        if (el && el.parentNode === document.body) {
            document.body.removeChild(el);
        }
    },
}
</script>

<style>
.widget-filter-panel {
    position: fixed;
    z-index: 9999;
    width: 400px;
    max-height: 520px;
}

.cdb-explain-menu-trigger.v-btn {
    width: 26px;
    height: 26px;
}

/* UXB-5: edit-mode authoring chrome (drag grip + kebab) reveals on card hover/focus.
   Active only when the card carries .cdb-hover-chrome (builder-v1 + edit mode). Opacity
   (not display:none) keeps the controls keyboard-focusable and the grip draggable. */
.cdb-hover-chrome .cdb-edit-chrome {
    opacity: 0.5;
    transition: opacity 0.15s ease;
}
.cdb-hover-chrome:hover .cdb-edit-chrome,
.cdb-hover-chrome:focus-within .cdb-edit-chrome {
    opacity: 1;
}

/* W7 DELTA-1: in VIEW mode (builder-v1) the per-widget toolbar (filter + refresh) rests
   hidden and reveals on card hover / focus-within — matching the showcase's clean card
   head (.tools: opacity 0 -> 1). Opacity + pointer-events (never display:none) keeps the
   buttons keyboard-focusable and preserves their 44px touch targets. Edit-mode chrome is
   untouched: its wrapper is .cdb-hover-chrome, not present in view mode, so .cdb-view-tools
   stays inert there and the filter/refresh keep today's always-visible edit treatment. */
.cdb-view-hover-chrome .cdb-view-tools {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.18s ease-out;
}
.cdb-view-hover-chrome:hover .cdb-view-tools,
.cdb-view-hover-chrome:focus-within .cdb-view-tools {
    opacity: 1;
    pointer-events: auto;
}

/* W7 DELTA-2: in VIEW mode the toolbar row is lifted out of flow (absolute, top-right)
   so it reserves NO vertical height — every card's title then sits at the same tight top
   offset matching the showcase (.card__head), instead of being pushed down by the hidden
   toolbar's reserved row. Anchored to the .cdb-view-hover-chrome root (position:relative).
   Gated to VIEW mode only: in edit mode the row keeps the drag grip + kebab in normal flow.
   Flag-off has neither class, so the row stays in flow, byte-identical. */
.cdb-view-hover-chrome {
    position: relative;
}
.cdb-view-toolrow {
    position: absolute;
    right: 8px;
    width: auto;
    margin: 0;
    z-index: 3;
}

/* Edit-mode counterpart of W7 DELTA-2: in builder-v1 EDIT mode the toolbar row is lifted
   out of flow (absolute, full width) so the drag grip stays top-LEFT and the tools stay
   top-RIGHT on ONE row, and the row reserves NO vertical height. The child widget head
   (title + Live badge / subtitle) then rises to the same tight top offset view mode
   achieves — sitting BESIDE the grip instead of below it. Anchored to the .cdb-hover-chrome
   root (position:relative). Gated to edit mode only: .cdb-hover-chrome is present only when
   editMode, so view mode (.cdb-view-hover-chrome) is untouched. */
.cdb-hover-chrome {
    position: relative;
}
.cdb-edit-toolrow {
    position: absolute;
    top: 6px;
    left: 0;
    right: 0;
    width: auto;
    margin: 0;
    padding: 0 8px;
    z-index: 3;
}
/* Clear the grip horizontally: the drag grip rests at the far top-left, so inset the child
   widget head by the grip width + a small gap and the title starts just right of the grip on
   the same row. This <style> is NOT scoped, so a plain descendant selector reaches into the
   child dashboard-widget's .cdb-widget-head; scoping it under .cdb-hover-chrome keeps it to
   edit mode only. */
.cdb-hover-chrome .cdb-widget-head {
    padding-left: 30px;
}

/* Showcase restyle (builder-v1, plan W7): the drag grip + kebab glyph rest in the
   showcase's neutral grey and warm to castleton on hover — accent reserved for the
   interaction. Flag-off keeps today's inherited color. */
.cdb-builder-v1 .widget-drag-handle {
    color: var(--v-text-base);
}
.cdb-builder-v1 .widget-drag-handle:hover {
    color: var(--v-primary-base);
}

/* AVA-602: "Register not configured" — a calm, neutral empty-state (no error hue), same
   centred anatomy and text tokens as the preview's error state (SqlViewWidgetPreview .svp-error). */
.cdb-config-required {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--ds-space-xs);
    height: 100%;
    padding: var(--ds-space-md);
    text-align: center;
}
.cdb-config-required__icon.v-icon {
    color: var(--v-text-base);
    opacity: 0.45;
    margin-bottom: var(--ds-space-xs);
}
.cdb-config-required__title {
    font-size: var(--ds-font-body-sm);
    font-weight: 600;
    color: var(--v-text-base);
}
.cdb-config-required__text {
    max-width: 320px;
    font-size: var(--ds-font-caption);
    line-height: 1.4;
    color: var(--v-text-base);
    opacity: 0.7;
}
</style>
